import { inject, Injectable } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { CanActivate, Router } from "@angular/router";
import { of } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";

import { AuthModel } from "@intocare/auth-data";
import { CONTEXT_TOKEN, ContextNotInitializedError, UnsupportedHqContextPropertyError } from "@intocare/util";

import { PrivacyModalService } from "../services/privacy-modal.service";

@Injectable({
	providedIn: "root",
})
export class PrivacyGuard implements CanActivate {
	private auth = inject(AuthModel);
	private router = inject(Router);
	private privacyService = inject(PrivacyModalService);

	private readonly contextService = inject(CONTEXT_TOKEN);

	private readonly context$ = toObservable(this.contextService.context);

	canActivate() {
		return this.context$.pipe(
			map((context) => {
				try {
					return {
						acceptedTermsAndConditions: context.acceptedTermsAndConditions,
						userId: context.user.id,
						hq: context.hq,
						home: context.currentSite.home,
					};
				} catch (error) {
					if (error instanceof ContextNotInitializedError) {
						return null;
					}

					if (error instanceof UnsupportedHqContextPropertyError) {
						return {
							acceptedTermsAndConditions: context.acceptedTermsAndConditions,
							userId: context.user.id,
							hq: context.hq,
							home: undefined,
						};
					}

					throw error;
				}
			}),
			filter((context) => context !== null),
			switchMap((context) => {
				if (!context.acceptedTermsAndConditions) {
					return this.privacyService.openTermsAndConditionsModal(context.hq, context.userId).pipe(
						switchMap((privacy) => {
							if (privacy) {
								return this.auth.refreshContext(context.hq, context.home).pipe(map(() => true));
							}

							return this.auth.logOut(false, { redirect: false }).pipe(map(() => this.router.createUrlTree([""])));
						}),
					);
				}
				return of(true);
			}),
		);
	}
}
